import { useState, useCallback } from 'react';

export const useZoomState = () => {
  const [zoom, setZoom] = useState(1.0);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [collapsedNodes, setCollapsedNodes] = useState(new Set());
  // Nodes the user has explicitly expanded past the genealogy page's
  // depth-limit default (see Genealogy.jsx's MAX_VISIBLE_DEPTH) -- kept
  // separate from collapsedNodes so a depth-based auto-collapse doesn't
  // fight with a manual expand click on the same node.
  const [manuallyExpandedNodes, setManuallyExpandedNodes] = useState(new Set());

  const getZoomLevel = useCallback(() => {
    if (zoom < 0.5) return 'zoomed_out';
    if (zoom < 1) return 'medium';
    return 'close';
  }, [zoom]);

  const toggleNodeCollapse = useCallback((nodeId) => {
    setCollapsedNodes((prev) => {
      const newCollapsed = new Set(prev);
      if (newCollapsed.has(nodeId)) {
        newCollapsed.delete(nodeId);
      } else {
        newCollapsed.add(nodeId);
      }
      return newCollapsed;
    });
  }, []);

  // Toggle for a node whose collapsed state is currently coming from
  // the depth-limit default (not from collapsedNodes itself) -- flips
  // manuallyExpandedNodes instead, so the depth default can be
  // overridden per-node without touching collapsedNodes' own manual
  // collapse tracking.
  const toggleManualExpand = useCallback((nodeId) => {
    setManuallyExpandedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  }, []);

  const zoomIn = useCallback(() => {
    setZoom((prev) => Math.min(3, prev * 1.2));
  }, []);

  const zoomOut = useCallback(() => {
    setZoom((prev) => Math.max(0.25, prev * 0.833));
  }, []);

  const resetZoom = useCallback(() => {
    setZoom(1.0);
  }, []);

  const centerOnNode = useCallback((x, y, containerWidth, containerHeight) => {
    setPan({
      x: containerWidth / 2 - x * zoom,
      y: containerHeight / 2 - y * zoom,
    });
  }, [zoom]);

  return {
    zoom,
    setZoom,
    pan,
    setPan,
    selectedNodeId,
    setSelectedNodeId,
    collapsedNodes,
    toggleNodeCollapse,
    manuallyExpandedNodes,
    toggleManualExpand,
    getZoomLevel,
    zoomIn,
    zoomOut,
    resetZoom,
    centerOnNode,
  };
};
